import * as React from "react";
import { useId, useState, useEffect, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";

import { srOnly } from "@labxchange/design_tokens";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

import { MultiSelectQuestionProps, CheckboxState } from "./types";
import { fetchTranslations } from "./locales/config";
import {
  CheckboxGroup,
  getOptionLetter,
  messages as checkboxMessages,
} from "./CheckboxGroup";
import { Figure } from "../Figure";
import { Collapsible } from "../LxCollapsible";
import { LxButton } from "../LxButton";
import { ActionAnnouncer } from "../ActionAnnouncer";
import { FeedbackBanner } from "../FeedbackBanner";
import { feedbackBannerWrapper } from "../FeedbackBanner/FeedbackBanner.css";
import { TableQuestion } from "../TableQuestion";
import { useMultiSelect } from "./useMultiSelect";
import {
  multiSelectQuestionClass,
  containerClass,
  questionSectionClass,
  questionHeaderClass,
  questionCategoryClass,
  questionTextClass,
  selectAllTextClass,
  optionsSectionClass,
  buttonSlabClass,
  attemptsTextClass,
  collapsibleClass,
  feedbackWrapperClass,
} from "./MultiSelectQuestion.css";

/* ---------------------------- Internationalization ---------------------------- */

export const messages = defineMessages({
  attemptsLeft: {
    id: "MultiSelectQuestion.attemptsLeft",
    defaultMessage: "Attempts left: {attemptsLeft}",
    description:
      "Label showing the number of attempts left for the question. {attemptsLeft} is a placeholder for the actual number.",
  },
  submitAnswer: {
    id: "MultiSelectQuestion.submitAnswer",
    defaultMessage: "Submit answer",
    description: "Text for the button to submit the selected answers.",
  },
  submitPrediction: {
    id: "MultiSelectQuestion.submitPrediction",
    defaultMessage: "Submit prediction",
    description: "Text for the button to submit the selected prediction.",
  },
  tryAgain: {
    id: "MultiSelectQuestion.tryAgain",
    defaultMessage: "Try again",
    description: "Text for the button to try again after incorrect submission.",
  },
  resetQuestion: {
    id: "MultiSelectQuestion.reset",
    defaultMessage: "Reset question",
    description: "Text for the button to reset the question and try again.",
  },
  resetSelection: {
    id: "MultiSelectQuestion.predictionReset",
    defaultMessage: "Reset prediction",
    description: "Text for the button to reset the question and try again.",
  },
  resetQuestionAriaLabel: {
    id: "MultiSelectQuestion.resetQuestionAriaLabel",
    defaultMessage: "Reset question button",
    description:
      "Aria label for the button to reset the question and try again.",
  },
  resetPredictionAriaLabel: {
    id: "MultiSelectQuestion.resetPredictionAriaLabel",
    defaultMessage: "Reset prediction button",
    description:
      "Aria label for the button to reset the prediction and try again.",
  },
  predictionSubmitted: {
    id: "MultiSelectQuestion.predictionSubmitted",
    defaultMessage:
      "Prediction submitted. You will compare this answer with your final result at the end of the experiment.",
    description:
      "Text shown in the banner when a prediction question has been submitted.",
  },
  multiSelectHeader: {
    id: "MultiSelectQuestion.multiSelectHeader",
    defaultMessage: "Multi-select Question",
    description: "Header text for multi-select question type.",
  },
  predictionHeader: {
    id: "MultiSelectQuestion.predictionHeader",
    defaultMessage: "Prediction Question",
    description: "Header text for prediction question type.",
  },
  incompleteWarningAnswer: {
    id: "MultiSelectQuestion.incompleteWarningAnswer",
    defaultMessage: "Please select at least one option to submit your answer.",
    description:
      "Warning shown when submit is clicked with no options selected (answer mode).",
  },
  incompleteWarningPrediction: {
    id: "MultiSelectQuestion.incompleteWarningPrediction",
    defaultMessage:
      "Please select at least one option to submit your prediction.",
    description:
      "Warning shown when submit is clicked with no options selected (prediction mode).",
  },
  selectAllThatApply: {
    id: "MultiSelectQuestion.selectAllThatApply",
    defaultMessage: "(Select all that apply.)",
    description:
      "Instruction text telling users they can select multiple options.",
  },
  correctAnswer: {
    id: "MultiSelectQuestion.correctAnswer",
    defaultMessage: "<b>Correct!</b>",
    description:
      "Feedback message shown when all correct answers are selected.",
  },
  incorrectAnswer: {
    id: "MultiSelectQuestion.incorrectAnswer",
    defaultMessage:
      "<b>Not quite.</b> You haven't selected the correct combination of answers.",
    description: "Feedback message shown when incorrect answers are selected.",
  },
  incorrectAnswerAnnouncement: {
    id: "MultiSelectQuestion.incorrectAnswerAnnouncement",
    defaultMessage:
      "Not quite. You haven't selected the correct combination of answers.",
    description:
      "Spoken variant of the incorrect feedback message, used in the screen-reader result announcement. Same wording as MultiSelectQuestion.incorrectAnswer without the bold markup, which a live region would otherwise speak as literal tag names.",
  },
  optionLabel: {
    id: "MultiSelectQuestion.optionLabel",
    defaultMessage: "Option {displayLabel}",
    description:
      "Names an option in the screen-reader result announcement, where {displayLabel} is A, B, C, etc.",
  },
  optionFeedbackAnnouncement: {
    id: "MultiSelectQuestion.optionFeedbackAnnouncement",
    defaultMessage: "{optionLabel}: {feedback}",
    description:
      "One option's entry in the screen-reader result announcement, pairing the option's name with its verdict and feedback.",
  },
});

/* ---------------------------- Helpers ---------------------------- */

/**
 * Reduce authored feedback markup to the string a screen reader should say.
 *
 * The visible feedback goes through `RichText`, which parses the markup into
 * real elements; the announcement is a text node, so the same markup written
 * there would be spoken as literal tag names. Rendering `RichText` a second time
 * inside the live region is not an option: it turns glossary markup into
 * focusable `role="button"` elements, which would duplicate tab stops.
 *
 * Line-break markup becomes a space first, because it renders as a break but
 * contributes no whitespace to `textContent` ("term<br/>matters" would be spoken
 * as one word). Inline tags deliberately do not, or emphasised words would split
 * ("the <b>in</b>dependent variable"). `DOMParser` then yields an inert document
 * — no script execution, no resource loads — so reading its `textContent` strips
 * the remaining tags and decodes entities in one safe step.
 */
const toSpokenText = (markup: string): string => {
  const withBreaks = markup.replace(/<br\s*\/?>|<\/(?:p|div|li)>/gi, " ");
  const text = new DOMParser().parseFromString(withBreaks, "text/html").body
    .textContent;
  return (text ?? "").replace(/\s+/g, " ").trim();
};

/* ---------------------------- UI Subcomponents ---------------------------- */

const QuestionHeader: React.FC<{
  id: string;
}> = ({ id }) => (
  <div className={questionHeaderClass}>
    <p id={id} className={questionCategoryClass}>
      <RichText value={messages.predictionHeader} />
    </p>
  </div>
);

const QuestionFooter: React.FC<{
  attemptsLeft: number;
  showReset: boolean;
  showTryAgain: boolean;
  onSubmit: () => void;
  onReset: () => void;
  onTryAgain: () => void;
  isPredictionQuestion: boolean;
}> = ({
  attemptsLeft,
  showReset,
  showTryAgain,
  onSubmit,
  onReset,
  onTryAgain,
  isPredictionQuestion,
}) => {
  const { formatMessage } = useIntl();
  const submitTextId = isPredictionQuestion
    ? messages.submitPrediction.id
    : messages.submitAnswer.id;
  const resetTextId = isPredictionQuestion
    ? messages.resetSelection.id
    : messages.resetQuestion.id;
  const resetAriaLabel = formatMessage(
    isPredictionQuestion
      ? messages.resetPredictionAriaLabel
      : messages.resetQuestionAriaLabel
  );
  const submitButtonType = isPredictionQuestion ? "alternative" : "primary";

  // Submitting replaces Submit with Reset/Try again; focus the replacement so focus
  // doesn't drop to the body (WCAG 2.4.3). Only on the transition away from Submit.
  const slabRef = useRef<HTMLDivElement>(null);
  const buttonKind = showReset ? "reset" : showTryAgain ? "tryAgain" : "submit";
  const prevButtonKindRef = useRef(buttonKind);
  useEffect(() => {
    if (prevButtonKindRef.current === "submit" && buttonKind !== "submit") {
      slabRef.current?.querySelector("button")?.focus();
    }
    prevButtonKindRef.current = buttonKind;
  }, [buttonKind]);

  return (
    <div className={buttonSlabClass} data-name="Button slab" ref={slabRef}>
      {attemptsLeft > -1 && (
        // Plain visible text, deliberately NOT a live region: the remaining
        // attempts are folded into the single question-level result
        // announcement (see handleSubmit), so a second polite region here would
        // compete with it for the screen reader's attention.
        <p className={attemptsTextClass} data-testid="attempts-left">
          <RichText
            value={{
              ...messages.attemptsLeft,
              placeholderValues: { attemptsLeft },
            }}
          />
        </p>
      )}
      {showReset ? (
        <LxButton
          variant="secondary"
          text={resetTextId}
          ariaLabel={resetAriaLabel}
          size="large"
          onClickHandler={onReset}
        />
      ) : showTryAgain ? (
        <LxButton
          variant="alternative"
          text={messages.tryAgain.id}
          size="large"
          onClickHandler={onTryAgain}
          disabled={false}
        />
      ) : (
        <LxButton
          variant={submitButtonType}
          text={submitTextId}
          size="large"
          onClickHandler={onSubmit}
        />
      )}
    </div>
  );
};

/* ---------------------------- Main Component ---------------------------- */

const MultiSelectQuestionInternal: React.FC<MultiSelectQuestionProps> = ({
  id,
  content,
  question,
  text,
  image,
  dataTable,
  options,
  answerId,
  hintDetails,
  isPredictionQuestion = false,
  totalAttempts = -1,
  correctFeedback,
  onOptionToggle,
  onSubmit,
  onReset,
  setCompleted,
}) => {
  const [questionState, questionActions] = useMultiSelect(id);
  const { formatMessage } = useIntl();

  const { selectedOptionIds, submittedOptionIds } = questionState;

  // Component-specific state
  const [attemptsLeft, setAttemptsLeft] = useState<number>(totalAttempts);
  const [resetCount, setResetCount] = useState(0);
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);

  // Question-level result announcement, driven by two persistently-mounted
  // polite status regions written in ping-pong. On each submit the full result
  // (verdict + every submitted option's verdict and feedback + any remaining
  // attempts) is written into whichever region is currently empty, and the
  // other is cleared in the same commit. Because the target region was already
  // mounted and empty, the write is a genuine text *addition* the screen reader
  // announces every time — even when two consecutive results are byte-identical
  // (the classic 4.1.3 identical-string no-op) — with no timing hacks. Clearing
  // the other region is a removal, which the default aria-relevant
  // ("additions text") does not announce, so it is silent.
  //
  // This region — not the visible banner — owns the readout. The banner used to
  // be the only announcement channel via role="alert", but it is inserted
  // together with its wrapper in the same commit that moves focus to the
  // replacement button, so the alert was routinely flushed by the focus
  // announcement and never spoken. A polite region queues behind that focus
  // announcement instead of racing it.
  //
  // There is deliberately no auto-clear timer: a guessed duration can truncate
  // a long readout. The result is cleared on the next interaction (toggling an
  // option, Try again, or Reset) so a stale result never lingers as a
  // browse-mode stop.
  const [announcements, setAnnouncements] = useState<[string, string]>([
    "",
    "",
  ]);
  const nextRegionRef = useRef(0);

  const announceResult = (message: string) => {
    const target = nextRegionRef.current;
    nextRegionRef.current = target === 0 ? 1 : 0;
    setAnnouncements(target === 0 ? [message, ""] : ["", message]);
  };

  const clearAnnouncements = () => setAnnouncements(["", ""]);

  const optionsSectionRef = useRef<HTMLDivElement>(null);

  const elementId = useId();
  const headerId = `${elementId}-header`;
  const textId = `${elementId}-text`;
  const questionId = `${elementId}-question`;
  const imageId = `${elementId}-image`;
  const imageCaptionId = `${elementId}-caption`;
  const announcementId = `${elementId}-result-announcement`;
  const announcementId2 = `${elementId}-result-announcement-2`;

  /* ---------------------------- Derived Booleans ---------------------------- */

  const hasSubmittedAnswers = submittedOptionIds.length > 0;

  // Check if all correct answers are in the submitted list
  const hasAllCorrectAnswers = answerId.every((id) =>
    submittedOptionIds.includes(id)
  );

  // Check if any incorrect answers are in the submitted list
  const hasIncorrectAnswers = submittedOptionIds.some(
    (id) => !answerId.includes(id)
  );

  // Question is complete when all correct answers are submitted and no incorrect ones
  const hasCorrectAnswer = hasAllCorrectAnswers && !hasIncorrectAnswers;

  const showTryAgainButton =
    !isPredictionQuestion &&
    hasSubmittedAnswers &&
    !hasCorrectAnswer &&
    (attemptsLeft > 0 || attemptsLeft === -1);

  const showResetButton = isPredictionQuestion
    ? hasSubmittedAnswers
    : hasSubmittedAnswers && hasCorrectAnswer;

  // Label of the primary action button in its current state, fed to the
  // ActionAnnouncer so screen readers are told when Submit becomes Try again / Reset.
  const currentActionLabel = formatMessage(
    showResetButton
      ? isPredictionQuestion
        ? messages.resetSelection
        : messages.resetQuestion
      : showTryAgainButton
        ? messages.tryAgain
        : isPredictionQuestion
          ? messages.submitPrediction
          : messages.submitAnswer
  );

  const showPredictionFeedback = isPredictionQuestion && hasSubmittedAnswers;

  const showAssessmentFeedback =
    !isPredictionQuestion && hasSubmittedAnswers && !hasCorrectAnswer;
  const showSuccessFeedback =
    !isPredictionQuestion && hasSubmittedAnswers && hasCorrectAnswer;

  const isIncomplete = selectedOptionIds.length === 0;

  const isOptionSelectionDisabled =
    (hasCorrectAnswer && !isPredictionQuestion) ||
    (isPredictionQuestion && hasSubmittedAnswers) ||
    attemptsLeft === 0;

  const isCompleted =
    (hasCorrectAnswer && !isPredictionQuestion) ||
    (isPredictionQuestion && hasSubmittedAnswers) ||
    (attemptsLeft === 0 && hasSubmittedAnswers);

  // Notify completion
  useEffect(() => {
    if (isCompleted) {
      setCompleted?.(true);
    }
  }, [isCompleted, setCompleted]);

  const handleOptionToggleWithTryAgain = (optionId: number) => {
    if (showTryAgainButton) {
      questionActions.tryAgain();
    }
    questionActions.toggleOption(optionId);
    setShowIncompleteWarning(false);
    // Starting a new attempt: drop the previous result so it is not left behind
    // as a stale browse-mode stop (removals are silent — see `announcements`).
    clearAnnouncements();
    onOptionToggle?.(optionId);
  };

  /* ---------------------------- Helper Functions ---------------------------- */

  /**
   * The full spoken result for one submit: the question-level verdict, then each
   * submitted option's verdict and feedback, then any remaining attempts.
   *
   * Composed here rather than read back out of the DOM because the visible
   * pieces live in three places (the banner, the per-option paragraphs and the
   * attempts counter) — announcing each from its own live region is exactly what
   * made them compete and drop. Authored content strings are resolved as
   * translation ids (content ships its English text as the id) and everything is
   * reduced to plain text, since a live region speaks markup literally.
   */
  const buildResultAnnouncement = (
    submittedIds: number[],
    isCorrectAnswer: boolean,
    nextAttemptsLeft: number
  ): string => {
    // Authored content strings are their own translation ids, with the English
    // text as the fallback — the convention the other question components use
    // for content-supplied text (see TableQuestion's cells). formatMessage
    // returns an array of chunks rather than a string when the resolved message
    // carries rich-text tags, which authored feedback does; joining first is the
    // same normalisation RichText applies before it parses.
    const resolveAuthored = (value: string) => {
      const translated: unknown = formatMessage({
        id: value,
        defaultMessage: value,
      });
      return toSpokenText(
        Array.isArray(translated) ? translated.join("") : String(translated)
      );
    };

    // Same wording the banner shows, from the tag-free message ids: the
    // banner's own messages wrap the verdict in <b> for emphasis, which only
    // RichText can render — spoken, it would be "b Correct b".
    const verdict = isPredictionQuestion
      ? formatMessage(messages.predictionSubmitted)
      : isCorrectAnswer
        ? correctFeedback
          ? resolveAuthored(correctFeedback)
          : formatMessage(checkboxMessages.optionCorrect)
        : formatMessage(messages.incorrectAnswerAnnouncement);

    const optionParts = options
      .map((option, index) => ({ option, index }))
      .filter(
        ({ option }) => submittedIds.includes(option.id) && !!option.feedback
      )
      .map(({ option, index }) =>
        formatMessage(messages.optionFeedbackAnnouncement, {
          optionLabel: formatMessage(messages.optionLabel, {
            displayLabel: getOptionLetter(index),
          }),
          feedback: [
            // Prediction questions are not graded, so their options are
            // announced without a verdict — matching what is shown on screen.
            isPredictionQuestion
              ? ""
              : formatMessage(
                  answerId.includes(option.id)
                    ? checkboxMessages.optionCorrect
                    : checkboxMessages.optionIncorrect
                ),
            resolveAuthored(option.feedback),
          ]
            .filter(Boolean)
            .join(" "),
        })
      );

    // Only on an incorrect submit with a limited budget: after a correct answer
    // the question is solved and "attempts left" would read as noise.
    const attemptsText =
      !isPredictionQuestion && !isCorrectAnswer && nextAttemptsLeft > -1
        ? formatMessage(messages.attemptsLeft, {
            attemptsLeft: nextAttemptsLeft,
          })
        : "";

    return [verdict, ...optionParts, attemptsText].filter(Boolean).join(" ");
  };

  const getCheckboxState = (optionId: number): CheckboxState => {
    if (isOptionSelectionDisabled && hasCorrectAnswer) {
      const isCorrect = answerId.includes(optionId);
      return isCorrect ? "correct" : "default";
    }

    if (isPredictionQuestion && hasSubmittedAnswers) {
      const isSubmitted = submittedOptionIds.includes(optionId);
      return isSubmitted ? "selected" : "default";
    }

    if (isOptionSelectionDisabled) return "disabled";

    const isSelected = selectedOptionIds.includes(optionId);
    return isSelected ? "selected" : "default";
  };

  /* ---------------------------- Handlers ---------------------------- */

  const handleSubmit = () => {
    if (isIncomplete) {
      setShowIncompleteWarning(true);
      return;
    }

    if (selectedOptionIds.length > 0) {
      const allCorrectSelected = answerId.every((id) =>
        selectedOptionIds.includes(id)
      );
      const noIncorrectSelected = selectedOptionIds.every((id) =>
        answerId.includes(id)
      );
      const isCorrectAnswer = allCorrectSelected && noIncorrectSelected;
      const willRunOutOfAttempts = attemptsLeft === 1;

      const willBeCompleted =
        isCorrectAnswer || willRunOutOfAttempts || isPredictionQuestion;

      // The attempts counter after this submit, mirroring the reducer below.
      // Computed here so the remaining-attempts sentence can be folded into the
      // single result announcement (the footer <p> is no longer a live region).
      const nextAttemptsLeft =
        attemptsLeft > 0 ? attemptsLeft - 1 : attemptsLeft === -1 ? -1 : 0;

      setAttemptsLeft((prev) => {
        if (prev > 0) return prev - 1;
        if (prev === -1) return -1;
        return 0;
      });

      questionActions.submit();

      // The submitted set after this dispatch — the slice appends the current
      // selection to the already-submitted list — so the announcement covers
      // exactly the options whose feedback becomes visible.
      const submittedAfter = Array.from(
        new Set([...submittedOptionIds, ...selectedOptionIds])
      );
      announceResult(
        buildResultAnnouncement(
          submittedAfter,
          isCorrectAnswer,
          nextAttemptsLeft
        )
      );

      if (willBeCompleted) {
        setCompleted?.(true);
      }

      onSubmit?.();
    }
  };

  const handleReset = () => {
    questionActions.reset();
    setAttemptsLeft(totalAttempts);
    setResetCount((prev) => prev + 1);
    setShowIncompleteWarning(false);
    clearAnnouncements();
    onReset?.();
  };

  const handleTryAgain = () => {
    questionActions.tryAgain();
    clearAnnouncements();
    onReset?.();
  };

  /* ---------------------------- Dynamic Content ---------------------------- */

  // Label the options group with the question (and any passage) only. The
  // question image is intentionally NOT folded into the group name: doing so
  // makes a screen reader read the image's alt text concatenated onto the first
  // option. The image stays in reading order via <Figure> and is announced on
  // its own as the user navigates the prompt.
  const buildAriaLabelledby = () => {
    const ids: string[] = [headerId];
    if (text) ids.push(textId);
    ids.push(questionId);
    return ids.join(" ");
  };

  const ariaLabelledby = buildAriaLabelledby();

  const renderContent = () => {
    const elements: React.ReactNode[] = [];

    content.forEach((type, index) => {
      switch (type) {
        case "text":
          if (text) {
            elements.push(
              <div
                key={`text-${index}`}
                id={textId}
                className={questionTextClass}
              >
                <RichText value={text} />
              </div>
            );
          }
          break;
        case "question":
          elements.push(
            <div
              key={`q-${index}`}
              className={questionSectionClass}
              data-name="Question"
            >
              {isPredictionQuestion && <QuestionHeader id={headerId} />}
              <div id={questionId} className={questionTextClass}>
                <RichText value={question} />
              </div>
            </div>
          );
          break;
        case "image":
          if (image) {
            elements.push(
              <Figure
                key={`img-${index}`}
                src={image.src}
                altText={image.altText}
                caption={image.caption}
                imageId={imageId}
                captionId={image.caption ? imageCaptionId : undefined}
                containerBgColor={image.containerBgColor}
              />
            );
          }
          break;
        case "dataTable":
          if (dataTable) {
            elements.push(
              <TableQuestion
                key={`table-${index}`}
                id={index}
                type="DATA_TABLE"
                data={dataTable.data}
                content={["table"]}
                isTransparent={true}
              />
            );
          }
          break;
        case "options":
          elements.push(
            <p key={`select-all-${index}`} className={selectAllTextClass}>
              <RichText value={messages.selectAllThatApply} />
            </p>
          );
          elements.push(
            <div
              key={`opt-${index}`}
              className={optionsSectionClass}
              data-name="Options"
              ref={optionsSectionRef}
            >
              <CheckboxGroup
                options={options}
                selectedOptionIds={selectedOptionIds}
                submittedOptionIds={submittedOptionIds}
                onOptionToggle={
                  isOptionSelectionDisabled
                    ? () => {
                        /* no-op */
                      }
                    : handleOptionToggleWithTryAgain
                }
                getCheckboxState={getCheckboxState}
                ariaLabelledby={ariaLabelledby}
                resetCount={resetCount}
                isPredictionQuestion={isPredictionQuestion}
                disabled={isOptionSelectionDisabled}
                isOptionCorrect={(optionId) => answerId.includes(optionId)}
                showVerdict={!isPredictionQuestion}
                idPrefix={elementId}
              />
            </div>
          );
          break;
        case "hintDetails":
          if (hintDetails) {
            elements.push(
              <div key={`hint-${index}`} className={collapsibleClass}>
                <Collapsible
                  label={hintDetails.label}
                  content={hintDetails.content}
                  open={hintDetails.open}
                />
              </div>
            );
          }
          break;
        default:
          break;
      }
    });

    return elements;
  };

  /* ---------------------------- Render ---------------------------- */

  return (
    <div className={multiSelectQuestionClass} data-name="Multi-select question">
      <div className={containerClass}>
        {/*
          Two question-level status regions written in ping-pong. Both stay
          mounted (empty) so the screen reader has stable regions to observe. On
          submit the whole result is written into whichever region is empty and
          the other is cleared, so every result — including one identical to the
          previous attempt — is a fresh addition that announces natively, while
          the clear is a silent removal. See the `announcements` state for why
          this owns the readout instead of the visible banner.
        */}
        <div
          id={announcementId}
          className={srOnly}
          role="status"
          aria-live="polite"
          data-testid={announcementId}
        >
          {announcements[0]}
        </div>
        <div
          id={announcementId2}
          className={srOnly}
          role="status"
          aria-live="polite"
          data-testid={announcementId2}
        >
          {announcements[1]}
        </div>
        {renderContent()}
        {(showSuccessFeedback || showAssessmentFeedback) && (
          <div className={feedbackWrapperClass} key={`feedback-${resetCount}`}>
            {/*
              alertRole={false} on both verdict banners: the graded result is
              announced by the polite status region above, which also reads the
              per-option feedback. Left as an alert, the banner is inserted in
              the same commit that moves focus to the replacement button, so it
              competed with (and was routinely flushed by) the focus
              announcement. It stays visible and reachable in browse mode.
            */}
            {showSuccessFeedback && (
              <FeedbackBanner
                variant="success"
                alertRole={false}
                testId="correct-feedback"
                message={correctFeedback ?? messages.correctAnswer}
              />
            )}
            {showAssessmentFeedback && (
              <FeedbackBanner
                variant="fail"
                alertRole={false}
                testId="incorrect-feedback"
                message={messages.incorrectAnswer}
              />
            )}
          </div>
        )}
        {showPredictionFeedback && (
          <div
            className={feedbackWrapperClass}
            key={`prediction-feedback-${resetCount}`}
          >
            {/* Announced by the status region above — see the verdict banners. */}
            <FeedbackBanner
              variant="information"
              alertRole={false}
              testId="prediction-feedback"
              message={messages.predictionSubmitted}
            />
          </div>
        )}
        {showIncompleteWarning && (
          <div className={feedbackBannerWrapper}>
            <FeedbackBanner
              variant="warning"
              // Stays an assertive alert: an incomplete submit produces no
              // result announcement and does not move focus, so there is
              // nothing for it to compete with.
              testId="incomplete-warning"
              message={
                isPredictionQuestion
                  ? messages.incompleteWarningPrediction
                  : messages.incompleteWarningAnswer
              }
            />
          </div>
        )}
        <ActionAnnouncer label={currentActionLabel} />
        <QuestionFooter
          attemptsLeft={attemptsLeft}
          showReset={showResetButton}
          showTryAgain={showTryAgainButton}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onTryAgain={handleTryAgain}
          isPredictionQuestion={isPredictionQuestion}
        />
      </div>
    </div>
  );
};

export const MultiSelectQuestion = (props: MultiSelectQuestionProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <MultiSelectQuestionInternal {...props} />
  </TranslationsProvider>
);
